// infcode_change - metrics foundation
import fs from "fs/promises"
import path from "path"
import { fileURLToPath, pathToFileURL } from "url"
import * as Log from "@opencode-ai/core/util/log"
import { installMetricsWasmConsoleBridge } from "./console-bridge"
import type { MetricsRuntimeModule } from "./runtime-types"
import { InfcodeHttp } from "../client/transport"

export type { MetricsRuntimeModule } from "./runtime-types"

const LOG_PREFIX = "[metrics:runtime]"
const log = Log.create({ service: LOG_PREFIX })

const SOURCE_JS = "event_tracking_rs/event_tracking_rs_bg.js"
const SOURCE_WASM = "event_tracking_rs/event_tracking_rs_bg.wasm"
const COMPILED_JS = "event_tracking_rs_bg.mjs"
const COMPILED_WASM = "event_tracking_rs_bg.wasm"

export type MetricsRuntimeMode = "source" | "compiled"

export type MetricsRuntimePaths = {
  mode: MetricsRuntimeMode
  js: string
  wasm: string
  importUrl: string
}

type ResolveInput = {
  mode?: MetricsRuntimeMode
  execPath?: string
  resolve?: (specifier: string) => string
}

type LoadInput = ResolveInput & {
  importer?: (specifier: string) => Promise<MetricsRuntimeModule>
  readFile?: (filepath: string) => Promise<BufferSource>
  instantiate?: (
    bytes: BufferSource,
    imports: WebAssembly.Imports,
  ) => Promise<WebAssembly.Instance | WebAssembly.WebAssemblyInstantiatedSource>
}

export namespace MetricsRuntime {
  let cached: Promise<MetricsRuntimeModule> | undefined

  export function resolvePaths(input: ResolveInput = {}): MetricsRuntimePaths {
    const mode = input.mode ?? detectMode()
    if (mode === "compiled") {
      const base = path.dirname(input.execPath ?? process.execPath)
      const js = path.join(base, COMPILED_JS)
      const wasm = path.join(base, COMPILED_WASM)
      return {
        mode,
        js,
        wasm,
        importUrl: pathToFileURL(js).href,
      }
    }

    const resolve = input.resolve ?? defaultResolve
    const js = toFilePath(resolve(SOURCE_JS))
    const wasm = toFilePath(resolve(SOURCE_WASM))
    return {
      mode,
      js,
      wasm,
      importUrl: pathToFileURL(js).href,
    }
  }

  export async function load(input: LoadInput = {}): Promise<MetricsRuntimeModule> {
    // WASM 日志经 console.* 写出；先桥到 Log，避免污染 TUI 终端。
    installMetricsWasmConsoleBridge()
    const paths = resolvePaths(input)
    try {
      const runtime = await (input.importer ?? defaultImport)(paths.importUrl)
      const wasmBytes = await (input.readFile ?? defaultReadFile)(paths.wasm)
      const bindings = { ...runtime } as unknown as Record<string, WebAssembly.ImportValue>
      // wasm-bindgen emits fetch(request) and receiver.fetch(request); hashes vary by SDK build.
      // Replace only this WASM instance's imports, leaving global fetch and the SDK module untouched.
      for (const [name, value] of Object.entries(bindings)) {
        if (!name.startsWith("__wbg_fetch_") || typeof value !== "function") continue
        if (value.length === 1) {
          bindings[name] = (request: Request) => InfcodeHttp.fetch(request)
        } else if (value.length === 2) {
          bindings[name] = (receiver: { fetch: InfcodeHttp.Fetch }, request: Request) =>
            InfcodeHttp.fetch(request, undefined, receiver.fetch.bind(receiver))
        } else {
          throw new Error(`Unsupported event tracking fetch import: ${name}`)
        }
      }
      const imports = {
        "./event_tracking_rs_bg.js": bindings,
      }
      const instantiated = await (input.instantiate ?? defaultInstantiate)(wasmBytes, imports)
      const instance = "instance" in instantiated ? instantiated.instance : instantiated
      if (typeof runtime.__wbg_set_wasm !== "function") {
        throw new Error("event tracking runtime missing __wbg_set_wasm")
      }
      runtime.__wbg_set_wasm(instance.exports)
      const maybeStart = (instance.exports as Record<string, unknown>)["__wbindgen_start"]
      if (typeof maybeStart === "function") {
        ;(maybeStart as () => void)()
      }
      return runtime
    } catch (err) {
      log.warn("failed to load metrics runtime", {
        mode: paths.mode,
        js: paths.js,
        wasm: paths.wasm,
        error: err instanceof Error ? err.message : String(err),
      })
      throw err
    }
  }

  export function loadCached(input: LoadInput = {}): Promise<MetricsRuntimeModule> {
    if (cached) return cached
    const loading = load(input).catch((err) => {
      if (cached === loading) cached = undefined
      throw err
    })
    cached = loading
    return loading
  }

  export function resetCache(): void {
    cached = undefined
  }
}

export const loadMetricsRuntime = MetricsRuntime.loadCached

function detectMode(): MetricsRuntimeMode {
  const entry = process.argv[1]
  if (!entry) return "compiled"
  if (entry === process.execPath) return "compiled"
  if (entry.startsWith("/$bunfs/") || entry.startsWith("B:/~BUN/")) return "compiled"
  return "source"
}

function defaultResolve(specifier: string) {
  return import.meta.resolve(specifier)
}

async function defaultImport(specifier: string) {
  return (await import(specifier)) as MetricsRuntimeModule
}

async function defaultReadFile(filepath: string): Promise<BufferSource> {
  const bytes = await fs.readFile(filepath)
  const copy = new Uint8Array(bytes.byteLength)
  copy.set(bytes)
  return copy
}

async function defaultInstantiate(
  bytes: BufferSource,
  imports: WebAssembly.Imports,
): Promise<WebAssembly.WebAssemblyInstantiatedSource> {
  return (await WebAssembly.instantiate(bytes, imports)) as unknown as WebAssembly.WebAssemblyInstantiatedSource
}

function toFilePath(value: string) {
  if (value.startsWith("file://")) return fileURLToPath(value)
  return value
}
